import React, { Fragment, useState } from 'react';

import BaseForm from '../../../../../components/form/BaseForm/BaseForm';
import CheckBox from '../../../../../components/input/CheckBox';
import Select from '../../../../../components/input/Select/Select';
import TextArea from '../../../../../components/input/TextArea/TextArea';
import { setClearDash, setQuickToDate, setQuickToDateTime, setTrim } from '../../../../../lib/common';
import { SearchOption, ViewPeriodType } from '../../types';
import { SearchType } from '../../../../../types/multiChannel/orderManagement';
import {
  CANCEL_DISPOSER_CHECKBOX_LIST,
  DEFAULT_PERIOD_SEARCH_OPTION,
  DEFAULT_SEARCH_OPTION,
  DEFAULT_SEARCH_TYPE,
  DETAIL_SEARCH_TYPE_LIST,
  ORDER_STATUS_CHECKBOX_LIST,
  PERIOD_SEARCH_TYPE,
  PERIOD_SEARCH_TYPE_SELECT_LIST,
} from '../../../../../constants/service/app/multiChannelConstants';
import { OrderManagementSearchBoxStyle } from './style';
import { ORDER_SEARCH_PERIOD_BUTTON_LIST } from '../../../../../constants/service/app/orderConstants';
import ButtonList from '../../../../../components/ButtonList';
import { DATE_FORMAT, QUICK_DATE_SELECT_BUTTON_TARGET } from '../../../../../constants/service/common/utilConstants';
import DateRangePicker from '../../../../../components/datePicker/DateRangePicker';
import { ValueOf } from '../../../../../types/common/utils';
import moment from '../../../../../lib/moment';

const initializeCheckboxState = (checkboxList: { id: string }[]): Record<string, boolean> => {
  return checkboxList.reduce(
    (acc, value) => ({
      ...acc,
      [value.id]: false,
    }),
    {},
  );
};

const getSelectedKeys = (selectedItems: Record<string, boolean>): string => {
  return Object.keys(selectedItems)
    .filter((key) => selectedItems[key])
    .join(',');
};

const INITIAL_STATE = {
  SELECTED_ORDER_STATUS: initializeCheckboxState(ORDER_STATUS_CHECKBOX_LIST),
  SELECTED_SEARCH_TYPE: DEFAULT_SEARCH_TYPE,
  SEARCH_VALUE: '',
  SELECTED_CANCEL_DISPOSER: initializeCheckboxState(CANCEL_DISPOSER_CHECKBOX_LIST),
  VIEW_PERIOD: DEFAULT_PERIOD_SEARCH_OPTION,
};

interface OrderManagementSearchBoxProps {
  onSearch: (param: SearchOption) => void;
}
const OrderManagementSearchBox = (props: OrderManagementSearchBoxProps): JSX.Element => {
  const { onSearch } = props;
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<Record<string, boolean>>(
    INITIAL_STATE.SELECTED_ORDER_STATUS,
  );
  const [selectedSearchType, setSelectedSearchType] = useState<SearchType>(INITIAL_STATE.SELECTED_SEARCH_TYPE);
  const [searchValue, setSearchValue] = useState<string>(INITIAL_STATE.SEARCH_VALUE);
  const [selectedCancelDisposer, setSelectedCancelDisposer] = useState<Record<string, boolean>>(
    INITIAL_STATE.SELECTED_CANCEL_DISPOSER,
  );
  const [viewPeriod, setViewPeriod] = useState<ViewPeriodType>(INITIAL_STATE.VIEW_PERIOD);

  const handleSearch = (): void => {
    const status = getSelectedKeys(selectedOrderStatus);
    const cancelDisposer = getSelectedKeys(selectedCancelDisposer);
    const totalPeriod = ORDER_SEARCH_PERIOD_BUTTON_LIST.find((item) => item.name === '전체');
    const startDate =
      totalPeriod?.id === viewPeriod.quickDateIndex
        ? null
        : moment(viewPeriod.period.startDate).startOf('day').toISOString();
    const endDate =
      totalPeriod?.id === viewPeriod.quickDateIndex
        ? null
        : moment(viewPeriod.period.endDate).endOf('day').toISOString();

    onSearch({
      status,
      searchType: selectedSearchType,
      searchValue,
      cancelDisposer,
      dateType: viewPeriod.type,
      startDate,
      endDate,
    });
  };

  const handleClear = (): void => {
    setSelectedOrderStatus(INITIAL_STATE.SELECTED_ORDER_STATUS);
    setSelectedSearchType(INITIAL_STATE.SELECTED_SEARCH_TYPE);
    setSearchValue(INITIAL_STATE.SEARCH_VALUE);
    setSelectedCancelDisposer(INITIAL_STATE.SELECTED_CANCEL_DISPOSER);
    setViewPeriod(INITIAL_STATE.VIEW_PERIOD);

    onSearch(DEFAULT_SEARCH_OPTION);
  };

  return (
    <OrderManagementSearchBoxStyle onSearch={handleSearch} onClear={handleClear}>
      <BaseForm
        label="주문 상태"
        content={
          <Fragment>
            {ORDER_STATUS_CHECKBOX_LIST.map((item) => (
              <CheckBox
                key={item.id}
                value={item.id}
                text={item.name}
                checked={selectedOrderStatus[item.id]}
                onChange={(checked: boolean): void =>
                  setSelectedOrderStatus((prev) => ({ ...prev, [item.id]: checked }))
                }
              />
            ))}
          </Fragment>
        }
      />
      <BaseForm
        label="상세 검색"
        content={
          <Fragment>
            <Select
              data-testid="search-type"
              list={DETAIL_SEARCH_TYPE_LIST}
              onChange={setSelectedSearchType}
              value={selectedSearchType}
            />

            <TextArea
              value={searchValue}
              onChange={(value: string): void => setSearchValue(setTrim(setClearDash(value)))}
              onPressEnter={handleSearch}
              placeholder={`멀티채널 주문번호, 외부채널 관리번호, 상품ID, 브랜드ID를 검색하는 경우, ","구분자를 이용하여 여러 주문건을 검색할 수 있습니다.`}
            />
          </Fragment>
        }
      />
      <BaseForm
        label="취소 처리자"
        content={
          <Fragment>
            {CANCEL_DISPOSER_CHECKBOX_LIST.map((item) => (
              <CheckBox
                key={item.id}
                value={item.id}
                text={item.name}
                checked={selectedCancelDisposer[item.id]}
                onChange={(checked: boolean): void =>
                  setSelectedCancelDisposer((prev) => ({ ...prev, [item.id]: checked }))
                }
              />
            ))}
          </Fragment>
        }
      />
      <BaseForm
        label="조회 기간"
        content={
          <Fragment>
            <Select
              list={PERIOD_SEARCH_TYPE_SELECT_LIST}
              onChange={(id: ValueOf<typeof PERIOD_SEARCH_TYPE>): void =>
                setViewPeriod((prev) => ({
                  ...prev,
                  type: id,
                }))
              }
              value={viewPeriod.type}
              isTable={true}
            />

            {/*날짜 선택 빠른 키*/}
            <ButtonList
              className="quick-date-picker"
              current={viewPeriod.quickDateIndex}
              list={ORDER_SEARCH_PERIOD_BUTTON_LIST}
              onClick={(data: { id: number; name: string; value: number | null }): void =>
                setViewPeriod((prev) => ({
                  ...prev,
                  ...setQuickToDateTime({
                    id: data.id,
                    value: data.value,
                    target: QUICK_DATE_SELECT_BUTTON_TARGET.START_DATE,
                    period: prev.period,
                    type: prev.type,
                  }),
                }))
              }
            />

            <div className="date-search">
              <DateRangePicker
                value={viewPeriod.period}
                showTime={true}
                outFormat={DATE_FORMAT.FULL_DASH_FORMAT}
                format={DATE_FORMAT.FULL_DASH_FORMAT}
                btnTextFormat={DATE_FORMAT.FULL_DASH_FORMAT}
                startText={DATE_FORMAT.FULL_DASH_FORMAT}
                endText={DATE_FORMAT.FULL_DASH_FORMAT}
                onSelect={(period: { startDate: string; endDate: string }): void =>
                  setViewPeriod((prev) => ({
                    ...prev,
                    ...setQuickToDate({
                      period,
                      type: viewPeriod.type,
                    }),
                  }))
                }
              />
            </div>
          </Fragment>
        }
      />
    </OrderManagementSearchBoxStyle>
  );
};

export default OrderManagementSearchBox;
